import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Palette, PencilSimple, Trash, Star, Check,
  Swatches, CircleNotch,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

// Workspace Phase 2 — Brand Systems & Color Palettes.
// Two-tab management page. Every mutation is scoped to the authenticated
// user server-side (workspace_routes.py enforces ownership).

const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fb;
};
const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-amber-400/60";
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// ---------- Color swatch helpers ----------

function Swatch({ hex, size = 28, className = "" }) {
  const valid = hex && HEX.test(hex);
  return (
    <span
      className={`inline-block rounded-md border border-white/10 ${className}`}
      style={{ width: size, height: size, background: valid ? hex : "transparent",
               backgroundImage: valid ? "none" : "repeating-linear-gradient(45deg,#333,#333 4px,#555 4px,#555 8px)" }}
      title={hex || "empty"}
    />
  );
}

function HexInput({ value, onChange, testid, placeholder = "#RRGGBB" }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value && HEX.test(value) ? value.length === 4 ? `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}` : value : "#000000"}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-9 w-10 rounded-md bg-transparent border border-white/10 cursor-pointer"
        data-testid={testid ? `${testid}-picker` : undefined}
      />
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        placeholder={placeholder}
        className={inputCls + " font-mono uppercase"}
        data-testid={testid ? `${testid}-hex` : undefined}
      />
    </div>
  );
}

// ---------- Brand editor dialog ----------

function BrandDialog({ dialog, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", description: "", primary_color: "", secondary_color: "",
    accent_color: "", text_color: "", background_color: "",
    font_display: "", font_body: "", tagline: "", guidelines: "", is_default: false,
  });
  useEffect(() => {
    if (!dialog) return;
    if (dialog.kind === "edit" && dialog.doc) {
      setF({
        name: dialog.doc.name || "",
        description: dialog.doc.description || "",
        primary_color: dialog.doc.primary_color || "",
        secondary_color: dialog.doc.secondary_color || "",
        accent_color: dialog.doc.accent_color || "",
        text_color: dialog.doc.text_color || "",
        background_color: dialog.doc.background_color || "",
        font_display: dialog.doc.font_display || "",
        font_body: dialog.doc.font_body || "",
        tagline: dialog.doc.tagline || "",
        guidelines: dialog.doc.guidelines || "",
        is_default: !!dialog.doc.is_default,
      });
    } else {
      setF({
        name: "", description: "", primary_color: "#FFD60A",
        secondary_color: "#111111", accent_color: "", text_color: "",
        background_color: "", font_display: "", font_body: "",
        tagline: "", guidelines: "", is_default: false,
      });
    }
  }, [dialog]);
  if (!dialog) return null;
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: typeof v === "string" ? v : v?.target?.value ?? v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Brand name is required."); return; }
    const payload = { ...f };
    for (const k of ["primary_color", "secondary_color", "accent_color", "text_color", "background_color"]) {
      if (payload[k] && !HEX.test(payload[k])) { toast.error(`${k.replace("_", " ")} must be a hex color.`); return; }
      if (!payload[k]) payload[k] = null;
    }
    try {
      if (dialog.kind === "edit") {
        await api.put(`/workspace/brands/${dialog.doc.id}`, payload);
        toast.success("Brand updated.");
      } else {
        await api.post("/workspace/brands", payload);
        toast.success("Brand created.");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't save brand.")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="brand-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">
          {dialog.kind === "edit" ? `Edit — ${dialog.doc.name}` : "New Brand System"}
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Brand name</label>
            <input data-testid="brand-name" value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder="Detroit Nights Co." className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Short description</label>
            <input data-testid="brand-desc" value={f.description} onChange={(e) => set("description")(e.target.value)} placeholder="Upscale nightlife promoter" className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">Colors</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["primary_color", "Primary"], ["secondary_color", "Secondary"],
                ["accent_color", "Accent"], ["text_color", "Text"],
                ["background_color", "Background"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="text-[10px] text-zinc-500 block mb-1">{label}</label>
                  <HexInput testid={`brand-color-${k}`} value={f[k]} onChange={set(k)} />
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Display font</label>
              <input data-testid="brand-font-display" value={f.font_display} onChange={(e) => set("font_display")(e.target.value)} placeholder="Bebas Neue" className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Body font</label>
              <input data-testid="brand-font-body" value={f.font_body} onChange={(e) => set("font_body")(e.target.value)} placeholder="Inter" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Tagline</label>
            <input data-testid="brand-tagline" value={f.tagline} onChange={(e) => set("tagline")(e.target.value)} placeholder="Nightlife, done right." className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Guidelines / creative notes</label>
            <textarea data-testid="brand-guidelines" rows={3} value={f.guidelines} onChange={(e) => set("guidelines")(e.target.value)} placeholder="e.g. Always keep the yellow warm; never use pure black backgrounds; typography must feel editorial." className={inputCls} />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input data-testid="brand-default" type="checkbox" checked={f.is_default} onChange={(e) => set("is_default")(e.target.checked)} />
            Mark as default brand (auto-applied in AI Pro Studio)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="brand-save-btn" onClick={save} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Save brand</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Palette editor dialog ----------

function PaletteDialog({ dialog, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", notes: "", colors: [{ hex: "#FFD60A", name: "" }] });
  useEffect(() => {
    if (!dialog) return;
    if (dialog.kind === "edit" && dialog.doc) {
      setF({
        name: dialog.doc.name || "",
        notes: dialog.doc.notes || "",
        colors: (dialog.doc.colors || []).map((c) => ({ hex: c.hex || "", name: c.name || "" })),
      });
    } else {
      setF({ name: "", notes: "", colors: [{ hex: "#FFD60A", name: "" }] });
    }
  }, [dialog]);
  if (!dialog) return null;
  const updateColor = (i, patch) => setF((s) => ({ ...s, colors: s.colors.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));
  const addColor = () => setF((s) => s.colors.length >= 12 ? s : { ...s, colors: [...s.colors, { hex: "#FFFFFF", name: "" }] });
  const removeColor = (i) => setF((s) => s.colors.length <= 1 ? s : { ...s, colors: s.colors.filter((_, idx) => idx !== i) });
  const save = async () => {
    if (!f.name.trim()) { toast.error("Palette name is required."); return; }
    for (const c of f.colors) {
      if (!c.hex || !HEX.test(c.hex)) { toast.error(`Invalid hex: '${c.hex || "(empty)"}'`); return; }
    }
    try {
      const payload = { name: f.name, notes: f.notes, colors: f.colors };
      if (dialog.kind === "edit") {
        await api.put(`/workspace/palettes/${dialog.doc.id}`, payload);
        toast.success("Palette updated.");
      } else {
        await api.post("/workspace/palettes", payload);
        toast.success("Palette created.");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't save palette.")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="palette-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">
          {dialog.kind === "edit" ? `Edit — ${dialog.doc.name}` : "New Color Palette"}
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Palette name</label>
            <input data-testid="palette-name" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} placeholder="Sunset Bloom" className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">Colors ({f.colors.length}/12)</label>
            <div className="space-y-2">
              {f.colors.map((c, i) => (
                <div key={i} data-testid={`palette-color-row-${i}`} className="flex items-center gap-2">
                  <HexInput testid={`palette-color-${i}`} value={c.hex} onChange={(v) => updateColor(i, { hex: v })} />
                  <input
                    value={c.name}
                    onChange={(e) => updateColor(i, { name: e.target.value })}
                    placeholder="name (optional)"
                    data-testid={`palette-color-name-${i}`}
                    className={inputCls + " max-w-[140px]"}
                  />
                  <button
                    onClick={() => removeColor(i)}
                    disabled={f.colors.length <= 1}
                    data-testid={`palette-remove-color-${i}`}
                    className="h-9 w-9 shrink-0 rounded-lg hover:bg-red-500/20 text-zinc-500 hover:text-red-300 flex items-center justify-center disabled:opacity-30 disabled:hover:bg-transparent"
                  >
                    <Trash size={13} />
                  </button>
                </div>
              ))}
            </div>
            {f.colors.length < 12 && (
              <button data-testid="palette-add-color-btn" onClick={addColor} className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-white/15 hover:bg-white/5 text-xs">
                <Plus size={12} weight="bold" /> Add color
              </button>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Notes</label>
            <textarea data-testid="palette-notes" rows={2} value={f.notes} onChange={(e) => setF((s) => ({ ...s, notes: e.target.value }))} placeholder="warm → cool gradient; best for outdoor events" className={inputCls} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="palette-save-btn" onClick={save} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Save palette</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Main page ----------

export default function WorkspaceBrands() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("brands");
  const [brands, setBrands] = useState(null);
  const [palettes, setPalettes] = useState(null);
  const [brandDialog, setBrandDialog] = useState(null);
  const [paletteDialog, setPaletteDialog] = useState(null);

  const loadBrands = useCallback(async () => {
    try { const r = await api.get("/workspace/brands"); setBrands(r.data.brands || []); }
    catch (e) { toast.error(errMsg(e, "Couldn't load brands.")); setBrands([]); }
  }, []);
  const loadPalettes = useCallback(async () => {
    try { const r = await api.get("/workspace/palettes"); setPalettes(r.data.palettes || []); }
    catch (e) { toast.error(errMsg(e, "Couldn't load palettes.")); setPalettes([]); }
  }, []);
  useEffect(() => { if (tab === "brands") loadBrands(); else loadPalettes(); }, [tab, loadBrands, loadPalettes]);

  const deleteBrand = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/workspace/brands/${b.id}`); toast.success("Brand deleted."); loadBrands(); }
    catch (e) { toast.error(errMsg(e, "Couldn't delete.")); }
  };
  const deletePalette = async (p) => {
    if (!window.confirm(`Delete palette "${p.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/workspace/palettes/${p.id}`); toast.success("Palette deleted."); loadPalettes(); }
    catch (e) { toast.error(errMsg(e, "Couldn't delete.")); }
  };
  const setDefault = async (b) => {
    try { await api.put(`/workspace/brands/${b.id}`, { is_default: true }); toast.success("Default brand updated."); loadBrands(); }
    catch (e) { toast.error(errMsg(e, "Couldn't set default.")); }
  };

  return (
    <div data-testid="workspace-brands" className="min-h-screen bg-[#070707] text-white font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1240px] mx-auto px-6 h-16 flex items-center gap-3">
          <button data-testid="ws-brands-back-btn" onClick={() => navigate("/workspace")} className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"><ArrowLeft size={18} /></button>
          <Palette size={20} weight="fill" className="text-amber-300" />
          <span className="font-display font-extrabold text-lg tracking-tight">Brand Systems &amp; Palettes</span>
        </div>
      </header>

      <main className="max-w-[1240px] mx-auto px-6 py-8">
        <div className="flex items-center gap-1 border-b border-white/10 mb-6">
          <button data-testid="ws-brands-tab-brands" onClick={() => setTab("brands")}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === "brands" ? "text-white border-b-2 border-amber-400" : "text-zinc-400 hover:text-white"}`}>
            <Swatches weight={tab === "brands" ? "fill" : "regular"} size={16} /> Brand Systems
          </button>
          <button data-testid="ws-brands-tab-palettes" onClick={() => setTab("palettes")}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === "palettes" ? "text-white border-b-2 border-amber-400" : "text-zinc-400 hover:text-white"}`}>
            <Palette weight={tab === "palettes" ? "fill" : "regular"} size={16} /> Color Palettes
          </button>
          <div className="ml-auto">
            {tab === "brands" && (
              <button data-testid="ws-brands-new-btn" onClick={() => setBrandDialog({ kind: "new" })} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-400 text-black hover:bg-amber-300 text-sm font-bold transition-colors">
                <Plus size={14} weight="bold" /> New Brand
              </button>
            )}
            {tab === "palettes" && (
              <button data-testid="ws-palettes-new-btn" onClick={() => setPaletteDialog({ kind: "new" })} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-400 text-black hover:bg-amber-300 text-sm font-bold transition-colors">
                <Plus size={14} weight="bold" /> New Palette
              </button>
            )}
          </div>
        </div>

        {tab === "brands" && (
          brands === null ? (
            <p className="text-sm text-zinc-500 py-16 text-center"><CircleNotch size={16} className="inline animate-spin mr-2" /> Loading brands…</p>
          ) : brands.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center">
              <Swatches size={40} weight="duotone" className="mx-auto mb-3 text-zinc-700" />
              <p className="text-sm text-zinc-400 font-semibold">No brand systems yet</p>
              <p className="text-xs text-zinc-500 mt-1">Save your colors, fonts, and guidelines once — then apply them to any AI Pro Studio generation.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {brands.map((b) => (
                <div key={b.id} data-testid={`brand-card-${b.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden flex flex-col">
                  <div className="h-20 relative" style={{ background: b.primary_color ? `linear-gradient(135deg, ${b.primary_color} 0%, ${b.secondary_color || b.accent_color || b.primary_color} 100%)` : "linear-gradient(135deg, #232323, #131313)" }}>
                    {b.is_default && (
                      <span className="absolute top-2 right-2 text-[10px] font-black tracking-wide uppercase px-2 py-0.5 rounded-full bg-black/60 text-amber-300 inline-flex items-center gap-1">
                        <Star size={10} weight="fill" /> DEFAULT
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <p className="font-display font-bold text-[15px] truncate">{b.name}</p>
                    {b.description && <p className="text-xs text-zinc-500 truncate">{b.description}</p>}
                    <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                      {["primary_color", "secondary_color", "accent_color", "text_color", "background_color"].map((k) => b[k] ? (
                        <Swatch key={k} hex={b[k]} size={18} />
                      ) : null)}
                    </div>
                    {(b.font_display || b.font_body) && (
                      <p className="text-[11px] text-zinc-500 mt-3">
                        {b.font_display && <span>Display: <span className="text-zinc-300">{b.font_display}</span></span>}
                        {b.font_display && b.font_body && " · "}
                        {b.font_body && <span>Body: <span className="text-zinc-300">{b.font_body}</span></span>}
                      </p>
                    )}
                    <div className="mt-auto pt-3 flex items-center gap-1">
                      {!b.is_default && (
                        <button data-testid={`brand-set-default-${b.id}`} onClick={() => setDefault(b)} className="text-[11px] text-amber-300 hover:text-amber-200 mr-auto inline-flex items-center gap-1"><Star size={11} weight="fill" /> Set default</button>
                      )}
                      <button data-testid={`brand-edit-${b.id}`} onClick={() => setBrandDialog({ kind: "edit", doc: b })} className="ml-auto h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400"><PencilSimple size={13} /></button>
                      <button data-testid={`brand-delete-${b.id}`} onClick={() => deleteBrand(b)} className="h-7 w-7 rounded-full hover:bg-red-500/20 flex items-center justify-center text-zinc-500 hover:text-red-300"><Trash size={13} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "palettes" && (
          palettes === null ? (
            <p className="text-sm text-zinc-500 py-16 text-center"><CircleNotch size={16} className="inline animate-spin mr-2" /> Loading palettes…</p>
          ) : palettes.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center">
              <Palette size={40} weight="duotone" className="mx-auto mb-3 text-zinc-700" />
              <p className="text-sm text-zinc-400 font-semibold">No color palettes yet</p>
              <p className="text-xs text-zinc-500 mt-1">Save reusable palettes and apply them to any AI Pro Studio generation to keep colors consistent.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {palettes.map((p) => (
                <div key={p.id} data-testid={`palette-card-${p.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex flex-col">
                  <div className="flex items-start gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-[15px] truncate">{p.name}</p>
                      <p className="text-[11px] text-zinc-500">{(p.colors || []).length} color{(p.colors || []).length === 1 ? "" : "s"}</p>
                    </div>
                    <button data-testid={`palette-edit-${p.id}`} onClick={() => setPaletteDialog({ kind: "edit", doc: p })} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400"><PencilSimple size={13} /></button>
                    <button data-testid={`palette-delete-${p.id}`} onClick={() => deletePalette(p)} className="h-7 w-7 rounded-full hover:bg-red-500/20 flex items-center justify-center text-zinc-500 hover:text-red-300"><Trash size={13} /></button>
                  </div>
                  <div className="flex rounded-lg overflow-hidden h-14 mb-2">
                    {(p.colors || []).map((c, i) => (
                      <div key={i} className="flex-1 relative group" style={{ background: c.hex }} title={`${c.hex}${c.name ? " · " + c.name : ""}`}>
                        <span className="absolute inset-x-0 bottom-0 text-[9px] font-mono py-0.5 text-center bg-black/40 text-white opacity-0 group-hover:opacity-100">{c.hex}</span>
                      </div>
                    ))}
                  </div>
                  {p.notes && <p className="text-[11px] text-zinc-500 truncate">{p.notes}</p>}
                </div>
              ))}
            </div>
          )
        )}
      </main>

      <BrandDialog dialog={brandDialog} onClose={() => setBrandDialog(null)} onSaved={loadBrands} />
      <PaletteDialog dialog={paletteDialog} onClose={() => setPaletteDialog(null)} onSaved={loadPalettes} />
    </div>
  );
}
